import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  ArrowLeft,
  Users, 
  Target, 
  Lightbulb, 
  Rocket,
  TrendingUp,
  Award,
  Heart,
  Volume2,
  VolumeX,
  GraduationCap,
  MapPin
} from 'lucide-react';
import { ROUTES } from '@constants';
import Button from '@components/common/Button';
import founderStoryImg from '@images/work/founder-story.jpeg';
import classroomEngagementImg from '@images/work/classroom-engagement.jpg';
import entrepreneurEventImg from '@images/work/entrepreneur-event.jpg';
import event1Img from '@images/work/event-1.jpg';
import event2Img from '@images/work/event-2.jpg';
import professionalImg from '@images/work/professional.jpg';
import teamImg from '@images/work/team.jpg';
import session1Img from '@images/work/session1.jpeg';
import session2Img from '@images/work/session2.jpeg';
import session3Img from '@images/work/session3.jpeg';

const OurJourney = () => {
  const [isVisible, setIsVisible] = useState({});
  const [isMuted, setIsMuted] = useState(true);
  const sectionRefs = useRef({});
  const videoRef = useRef(null);

  useEffect(() => {
    const observers = {};
    
    Object.keys(sectionRefs.current).forEach(key => {
      if (sectionRefs.current[key]) {
        observers[key] = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible(prev => ({ ...prev, [key]: true }));
            }
          },
          { threshold: 0.1 }
        );
        observers[key].observe(sectionRefs.current[key]);
      }
    });

    return () => {
      Object.values(observers).forEach(observer => observer.disconnect());
    };
  }, []);


  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">

      {/* ═══════════════════════════════════════════════════════════════
          1. HERO — "Built From Experience, Not Just Ideas"
      ═══════════════════════════════════════════════════════════════ */}
      <section className="relative h-[70vh] sm:h-[75vh] md:h-[80vh] w-full flex items-center justify-center overflow-hidden px-4 sm:px-6 md:px-8 lg:px-12 pt-4 sm:pt-6 md:pt-8 lg:pt-12">
        {/* Marketing Video - Fullscreen */}
        <div className="w-full h-full relative">
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-lg"
            autoPlay
            muted={isMuted}
            loop
            playsInline
          >
            <source src="https://career-path-assets.s3.ap-south-1.amazonaws.com/public/WhatsApp+Video+2026-09-14+at+1.05.39+AM.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Mute/Unmute Button */}
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 group z-10"
            aria-label={isMuted ? "Unmute video" : "Mute video"}
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5 text-primary-600" />
            ) : (
              <Volume2 className="w-5 h-5 text-primary-600" />
            )}
          </button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          2. FOUNDER STORY
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-7xl mx-auto">
            
            {/* Left - Text */}
            <div 
              ref={setRef('founder-text')}
              className={`transition-all duration-1000 ${
                isVisible['founder-text'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-full mb-6">
                <Heart className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-primary-700">The Beginning</span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-primary-900 mb-6 leading-tight">
                Why We Started F.O.W.
              </h2>

              <div className="space-y-4 text-base sm:text-lg text-primary-700 leading-relaxed">
                <p>
                  Every day, thousands of students make career decisions based on incomplete information, 
                  peer pressure, or simply what seems "safe." We've seen the confusion, the anxiety, 
                  and the missed opportunities.
                </p>
                <p className="font-medium text-primary-900">
                  F.O.W. was born from a simple realization: students don't need more options — 
                  they need clarity.
                </p>
                <p>
                  Through countless conversations with students, educators, and industry professionals, 
                  we understood that career guidance isn't about pushing people toward specific paths. 
                  It's about giving them the tools, insights, and confidence to choose their own.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-3 px-4 py-3 bg-primary-50 rounded-xl">
                  <Users className="w-5 h-5 text-primary-600" />
                  <div>
                    <div className="text-sm font-bold text-primary-900">Student-First</div>
                    <div className="text-xs text-primary-600">Built for real needs</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 bg-secondary-50 rounded-xl">
                  <Target className="w-5 h-5 text-secondary-600" />
                  <div>
                    <div className="text-sm font-bold text-primary-900">Clarity-Driven</div>
                    <div className="text-xs text-primary-600">Not just information</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Image */}
            <div 
              ref={setRef('founder-image')}
              className={`relative transition-all duration-1000 delay-200 ${
                isVisible['founder-image'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-primary-200 aspect-[4/5] group">
                <img
                  src={founderStoryImg}
                  alt="F.O.W Founder - Our Story"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-secondary-200/30 rounded-full blur-2xl -z-10"></div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          3. WORK IN ACTION — Timeline Style
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          <div 
            ref={setRef('action-header')}
            className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-1000 ${
              isVisible['action-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-primary-900 mb-4">
              From Ideas to Real Conversations
            </h2>
            <p className="text-base sm:text-lg text-primary-700">
              Engaging directly with students, understanding their questions, and turning 
              real conversations into better career guidance.
            </p>
          </div>

          {/* Timeline Item */}
          <div className="max-w-6xl mx-auto">
            <div 
              ref={setRef('action-item')}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center transition-all duration-1000 delay-200 ${
                isVisible['action-item'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-primary-200 aspect-[16/11] group">
                <img
                  src={classroomEngagementImg}
                  alt="Student Engagement - Direct classroom interaction and career guidance"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              {/* Content */}
              <div>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full">
                    Student Engagement
                  </span>
                  <span className="px-3 py-1.5 bg-purple-50 text-purple-700 text-xs font-semibold rounded-full">
                    Career Awareness
                  </span>
                  <span className="px-3 py-1.5 bg-green-50 text-green-700 text-xs font-semibold rounded-full">
                    Mentorship
                  </span>
                  <span className="px-3 py-1.5 bg-orange-50 text-orange-700 text-xs font-semibold rounded-full">
                    Real-World Learning
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-primary-900 mb-4">
                  Direct Student Interaction
                </h3>
                <p className="text-base sm:text-lg text-primary-700 leading-relaxed mb-6">
                  Every classroom session, every question asked, and every concern raised helps us 
                  understand what students truly need. This isn't built in isolation — it's built 
                  through real conversations with real people facing real career decisions.
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-primary-900">On-Ground Experience</div>
                    <div className="text-xs text-primary-600">Building from real feedback</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          4. ENTREPRENEURSHIP & INDUSTRY
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 md:py-24 bg-primary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          <div 
            ref={setRef('entrepreneur-header')}
            className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-1000 ${
              isVisible['entrepreneur-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-primary-900 mb-4">
              Inside the Entrepreneurial Ecosystem
            </h2>
            <p className="text-base sm:text-lg text-primary-700">
              Building F.O.W. means constantly learning from founders, entrepreneurs, 
              innovators and the ecosystem around us.
            </p>
          </div>

          {/* Large Cinematic Image */}
          <div 
            ref={setRef('entrepreneur-image')}
            className={`max-w-6xl mx-auto transition-all duration-1000 delay-200 ${
              isVisible['entrepreneur-image'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-primary-200 aspect-[21/9] group">
              <img
                src={entrepreneurEventImg}
                alt="Entrepreneurship Ecosystem - Learning from founders and innovators"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Badge overlay */}
            
          </div>

        </div>
      </section>

      

      {/* ═══════════════════════════════════════════════════════════════
          5. SCHOOL & COLLEGE VISITS
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          <div 
            ref={setRef('campus-header')}
            className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-1000 ${
              isVisible['campus-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-full mb-6">
              <GraduationCap className="w-4 h-4 text-primary-600" />
              <span className="text-xs sm:text-sm font-semibold text-primary-700">Campus Visits</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-primary-900 mb-4">
              Invited to Schools & Colleges
            </h2>
            <p className="text-base sm:text-lg text-primary-700">
              We've been invited to schools and colleges to interact with students, 
              conduct sessions, and help them explore career paths with clarity.
            </p>
          </div>

          {/* Campus Visit Images Grid */}
          <div 
            ref={setRef('campus-grid')}
            className={`max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 delay-200 ${
              isVisible['campus-grid'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {[
              { img: session1Img, title: 'Career Guidance Session', location: 'School Visit' },
              { img: session2Img, title: 'Student Interaction', location: 'College Campus' },
              { img: session3Img, title: 'Mentorship Workshop', location: 'Campus Event' },
            ].map((item, index) => (
              <div 
                key={index}
                className="group relative rounded-2xl overflow-hidden shadow-xl border border-primary-200 aspect-[4/3]"
              >
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/70 via-primary-900/10 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1">{item.title}</h3>
                  <div className="flex items-center gap-1.5 text-white/80">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="text-xs sm:text-sm">{item.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          6. REAL-WORLD EXPOSURE — Staggered Grid
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          <div 
            ref={setRef('exposure-header')}
            className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-1000 ${
              isVisible['exposure-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-primary-900 mb-4">
              Learning From the People Who Are Building the Future
            </h2>
            <p className="text-base sm:text-lg text-primary-700">
              Every interaction, event, classroom and conversation helps us understand what 
              students need — and what the future of work actually looks like.
            </p>
          </div>

          {/* Staggered Image Grid */}
          <div 
            ref={setRef('exposure-grid')}
            className={`max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 delay-200 ${
              isVisible['exposure-grid'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Image 1 - Large */}
            <div className="sm:col-span-2 lg:col-span-2 lg:row-span-2">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-primary-200 aspect-[16/10] group">
                <img
                  src={event1Img}
                  alt="Professional Event - Building connections and learning"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>

            {/* Image 2 - Small */}
            <div className="lg:col-span-1">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-primary-200 aspect-square group">
                <img
                  src={event2Img}
                  alt="Event Moment - Industry engagement"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>

            {/* Image 3 - Small */}
            <div className="lg:col-span-1">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-primary-200 aspect-square group">
                <img
                  src={teamImg}
                  alt="Team Photo - Building together"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>

            {/* Image 4 - Medium */}
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-primary-200 aspect-[21/9] group">
                <img
                  src={professionalImg}
                  alt="Professional Engagement - Industry connections"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>

            {/* Image 5 - Small */}
            <div className="lg:col-span-1">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-primary-200 aspect-square group">
                <img
                  src={event1Img}
                  alt="Event Highlights - Learning and networking"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          7. IMPACT / CREDIBILITY SECTION
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          <div 
            ref={setRef('impact-header')}
            className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-1000 ${
              isVisible['impact-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">
              Real Impact, Real Numbers
            </h2>
            <p className="text-base sm:text-lg text-white/80">
              Building something meaningful takes time, effort, and real connections.
            </p>
          </div>

          <div 
            ref={setRef('impact-stats')}
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto transition-all duration-1000 delay-200 ${
              isVisible['impact-stats'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {[
              { count: 4000 ,label: 'Students Engaged', icon: Users, color: 'from-blue-400 to-blue-600' },
              { count: 1000,  label: 'Career Conversations', icon: Lightbulb, color: 'from-purple-400 to-purple-600' },
              {count: 100 , label: 'Institutions Connected', icon: Award, color: 'from-green-400 to-green-600' },
              { count: 50, label: 'Industry Ecosystem', icon: TrendingUp, color: 'from-orange-400 to-orange-600' }
            ].map((stat, index) => (
              <div 
                key={stat.label}
                className="text-center p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300"
              >
                <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center`}>
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl sm:text-5xl font-black text-white mb-2">{stat.count} +</div>
                <div className="text-sm sm:text-base font-semibold text-white/90">{stat.label}</div>
                {/* <div className="text-xs text-white/60 mt-2">Growing every day</div> */}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          8. FINAL CTA
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          <div 
            ref={setRef('cta')}
            className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${
              isVisible['cta'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-primary-900 mb-6 leading-tight">
              Your Career Shouldn't Be a Guess.
            </h2>
            
            <p className="text-lg sm:text-xl md:text-2xl text-primary-700 leading-relaxed mb-10 sm:mb-12">
              F.O.W. is building a future where every student can make career decisions with 
              <span className="font-bold text-primary-900"> clarity</span>, 
              <span className="font-bold text-primary-900"> confidence</span> and 
              <span className="font-bold text-primary-900"> context</span>.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={ROUTES.REGISTER}>
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-full sm:w-auto px-8 py-4 text-base sm:text-lg font-bold"
                >
                  Explore F.O.W. <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to={ROUTES.REGISTER}>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full sm:w-auto px-8 py-4 text-base sm:text-lg font-bold"
                >
                  Join the Journey
                </Button>
              </Link>
            </div>

            {/* Social proof */}
            
          </div>

        </div>
      </section>

    </div>
  );
};

export default OurJourney;
